import { CommonModule, NgForOf, NgIf } from "@angular/common";
import { Component, inject, Inject, NgModule, ViewChild } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule } from "@angular/material/list";
import { MAT_DIALOG_DATA, MatDialogModule } from "@angular/material/dialog";
import OrderAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order.entity";
import { lastValueFrom, take } from "rxjs";
import { UpdateOrderService } from "app/modules/components/sales/update-order/update-order.service";
import { Router } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import {
  FCanvasChangeEvent,
  FCanvasComponent,
  FFlowModule,
  FZoomDirective,
  EFConnectionType,
  EFConnectionBehavior,
} from "@foblex/flow";
import { BrowserService } from "@foblex/platform";
import { PointExtensions } from "@foblex/2d";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatOptionModule } from "@angular/material/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { DocumentNavigationService } from "app/common/services/document-navigation.service";
import OrderPaymentAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/process/order-payment/order-payment.entity";

interface Document {
  className: string;
  classCode: string;
  documentTypeName: string;
  serial: string;
  inputId?: string | null;
  outputId?: string | null;
  fInputConnectableSide?: string;
  fOutputConnectableSide?: string;
  fNodePosition?: { x: number; y: number };
  sourceTransactionSerials?: string[];
  nextTransactionSerials?: string[];
  level?: number;
  typeCode: string;
}

@Component({
  selector: "app-document-flow",
  templateUrl: "./document-flow.component.html",
  styleUrls: ["./document-flow.component.scss"],
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    MatIconModule,
    MatListModule,
    MatDialogModule,
    MatButtonModule,
    FFlowModule,
    MatCardModule,
    MatFormFieldModule,
    FZoomDirective,
    MatOptionModule,
    CommonModule,
    FFlowModule,
    MatTooltipModule
  ],
})
export class DocumentFlowComponent {

  @ViewChild(FCanvasComponent, { static: true }) fCanvas!: FCanvasComponent;

  public flowDocuments: Document[] = [];
  public type = EFConnectionType.STRAIGHT;
  public behavior = EFConnectionBehavior.FLOATING;

  public connections: ({
    outputId: string;
    inputId: string;
  })[] = [];

  constructor(
    private readonly updateOrderService: UpdateOrderService,
    private readonly router: Router,
    @Inject(MAT_DIALOG_DATA) public data: OrderAttributes,
    private readonly documentNavigationService: DocumentNavigationService
  ) {
  }

  private readonly _fBrowser = inject(BrowserService);


  protected onCanvasChanged(event: FCanvasChangeEvent): void {
    this._fBrowser.document.documentElement.style.setProperty(
      "--flow-scale",
      `${event.scale}`
    );
  }

  public ngOnDestroy(): void {
    this._fBrowser.document.documentElement.style.removeProperty(
      "--flow-scale"
    );
  }

  async ngOnInit() {
    this.getRelated();
  }

  async getRelated() {
    this.connections = [];
    // @ts-ignore
    const serialToSend = this.data?.serial ?? this?.data?.serials;

    const res = await lastValueFrom(
      this.updateOrderService
        .flowDocument({
          documentSerial: serialToSend,
          documentType: this.data?.type?.code,
        })
        .pipe(take(1))
    );

    const currentDocumentFlowData = res.data[0];
    const originalResponseAllDocuments = [
      ...(currentDocumentFlowData?.sourceDocuments ?? []),
      ...(currentDocumentFlowData?.nextDocuments ?? []),
    ];

    const currentDocFullInfo =
      originalResponseAllDocuments.find(
        (doc) =>
          doc?.serial === serialToSend &&
          doc?.class?.code === this.data?.class?.code
      ) ?? this.data;

    let allDocuments = [
      ...(currentDocumentFlowData?.sourceDocuments ?? []),
      currentDocFullInfo,
      ...(currentDocumentFlowData?.nextDocuments ?? []),
    ];

    if (this.data?.type?.code === DocumentTypeEnum.FACTURA && Array.isArray(currentDocumentFlowData?.payments)) {
      const paymentDocs = currentDocumentFlowData.payments.map((p: OrderPaymentAttributes) => ({
        className: p?.document_class?.name,
        classCode: p?.document_class?.code,
        documentTypeName: p?.document_type?.name,
        serial: p?.serials,
        inputId: null,
        outputId: null,
        fInputConnectableSide: "left",
        fOutputConnectableSide: "right",
        fNodePosition: { x: 0, y: 0 },
        typeCode: p?.document_type?.code,
        class: p?.document_class,
        type: p?.document_type,
        _id: p?._id,
      }));

      allDocuments = [...allDocuments, ...(paymentDocs as unknown as OrderAttributes[])];
    }

    const uniqueDocuments = this.removeDuplicateDocuments(allDocuments);

    const orderedDocuments = uniqueDocuments.sort(
      (a, b) =>
        new Date(a.creationDateTime || a.createdAt).getTime() -
        new Date(b.creationDateTime || b.createdAt).getTime()
    );

    this.flowDocuments = orderedDocuments.map((doc) => this.mapToDocument(doc));

    this.flowDocuments = this.getNodeProperties(this.flowDocuments) ?? [];

    this.generateConnections();

    if (this.data?.type?.code === DocumentTypeEnum.FACTURA && Array.isArray(currentDocumentFlowData?.payments)) {
      const facturaNode = this.flowDocuments.find((d) => this.isCurrentDocument(d));

      if (facturaNode && facturaNode.fNodePosition) {
        const totalPayments = currentDocumentFlowData.payments.length;

        const nodeHeightWithMargin = 450;
        const horizontalOffset = 450;

        currentDocumentFlowData.payments.forEach((p, index) => {
          const targetNode = this.flowDocuments.find((d) => d.serial === p.serials);

          if (targetNode) {
            this.connections.push({
              outputId: facturaNode.outputId!,
              inputId: targetNode.inputId!,
            });

            if (targetNode.fNodePosition) {
              if (totalPayments === 1) {
                targetNode.fNodePosition.y = facturaNode.fNodePosition.y + 10;
                targetNode.fNodePosition.x = facturaNode.fNodePosition.x + 350;
              } else {
                const centeredIndex = index - (totalPayments - 1) / 2;

                targetNode.fNodePosition.x = facturaNode.fNodePosition.x + horizontalOffset;

                targetNode.fNodePosition.y = facturaNode.fNodePosition.y + (centeredIndex * nodeHeightWithMargin);
              }
            }
          }
        });
      }
    } else {
      const facturaDoc = currentDocumentFlowData?.nextDocuments?.find(
        (doc) => doc?.type?.code === DocumentTypeEnum.FACTURA
      );

      if (facturaDoc && Array.isArray(facturaDoc?.payments)) {
        const paymentDocs = facturaDoc.payments.map((p: OrderPaymentAttributes) => ({
          className: p?.document_class?.name,
          classCode: p?.document_class?.code,
          documentTypeName: p?.document_type?.name,
          serial: p?.serials,
          inputId: null,
          outputId: null,
          fInputConnectableSide: "left",
          fOutputConnectableSide: "right",
          fNodePosition: { x: 0, y: 0 },
          typeCode: p?.document_type?.code,
          class: p?.document_class,
          type: p?.document_type,
          _id: p?._id,
        }));

        this.flowDocuments = [
          ...this.flowDocuments,
          ...(paymentDocs as unknown as Document[]),
        ];

        this.flowDocuments = this.getNodeProperties(this.flowDocuments) ?? [];

        const facturaNode = this.flowDocuments.find(
          (d) => d.serial === facturaDoc.serial && d.typeCode === facturaDoc.type.code
        );

        if (facturaNode && facturaNode.fNodePosition) {
          const totalPayments = facturaDoc.payments.length;
          const nodeHeightWithMargin = 450;
          const horizontalOffset = 450;

          facturaDoc.payments.forEach((p, index) => {
            const targetNode = this.flowDocuments.find((d) => d.serial === p.serials);

            if (targetNode) {
              this.connections.push({
                outputId: facturaNode.outputId!,
                inputId: targetNode.inputId!,
              });

              if (targetNode.fNodePosition) {
                if (totalPayments === 1) {
                  targetNode.fNodePosition.y = facturaNode.fNodePosition.y + 10;
                  targetNode.fNodePosition.x = facturaNode.fNodePosition.x + 350;
                } else {
                  const centeredIndex = index - (totalPayments - 1) / 2;
                  targetNode.fNodePosition.x =
                    facturaNode.fNodePosition.x + horizontalOffset;
                  targetNode.fNodePosition.y =
                    facturaNode.fNodePosition.y +
                    centeredIndex * nodeHeightWithMargin;
                }
              }
            }
          });
        }
      }
    }

    this.centerView();
  }


  public getNodeProperties(documentsList: Document[]): Document[] {
    let outputCount = 1;
    let inputCount = 1;

    documentsList.forEach((doc, index) => {
      const isFirst = index === 0;
      const isLast = index === documentsList.length - 1;
      let inputId = null;
      let outputId = null;

      if (!isFirst) {
        inputId = "inputid" + inputCount;
        inputCount++;
      }

      if (!isLast) {
        outputId = "outputid" + outputCount;
        outputCount++;
      }

      doc.inputId = inputId;
      doc.outputId = outputId;
      doc.fInputConnectableSide = "left";
      doc.fOutputConnectableSide = "right";

      doc.fNodePosition = {
        x: index * 450 + 100,
        y: 200
      };

    });

    return documentsList;
  }


  public generateConnections() {
    this.connections = [];

    this.flowDocuments
      .filter((doc) => doc.outputId)
      .forEach((sourceDoc) => {

        const nextSerials = sourceDoc.nextTransactionSerials ?? [];

        if (nextSerials.length > 0) {
          nextSerials.forEach((targetSerial) => {
            const targetDoc = this.flowDocuments.find((doc) => doc.serial === targetSerial);
            if (targetDoc && targetDoc.inputId) {
              this.connections.push({
                outputId: sourceDoc.outputId!,
                inputId: targetDoc.inputId,
              });
            }
          });
        }

        else {
          const DOCUMENTO_PAGO_CODE = DocumentTypeEnum.DOCUMENTO_DE_PAGO;

          if (sourceDoc.typeCode === DOCUMENTO_PAGO_CODE) {
            return;
          }

          const currentIndex = this.flowDocuments.findIndex(d => d.serial === sourceDoc.serial);

          if (currentIndex !== -1 && currentIndex < this.flowDocuments.length - 1) {
            const nextDoc = this.flowDocuments[currentIndex + 1];

            if (nextDoc?.typeCode === DOCUMENTO_PAGO_CODE) {
              return;
            }

            if (nextDoc && nextDoc.inputId) {
              this.connections.push({
                outputId: sourceDoc.outputId!,
                inputId: nextDoc.inputId
              });
            }
          }
        }
      });
  }

  mapToDocument(doc: OrderAttributes): Document {
    return {
      className: doc?.class?.name,
      classCode: doc?.class?.code,
      documentTypeName: doc?.type?.name,
      // @ts-ignore
      serial: doc?.serial ?? doc?.serials,
      inputId: null,
      outputId: null,
      fInputConnectableSide: "left",
      fOutputConnectableSide: "right",
      fNodePosition: {
        x: 0,
        y: 0
      },
      typeCode: doc?.type?.code
    }
  }

  private removeDuplicateDocuments(documents: OrderAttributes[]): OrderAttributes[] {
    const seen = new Set<string>();
    const uniqueDocuments: OrderAttributes[] = [];

    documents.forEach(doc => {
      const uniqueKey = [
        doc?.serial || '',
        doc?.type?.name || '',
        doc?.class?.name || '',
        doc?.class?.code || ''
      ].join('|');

      if (!seen.has(uniqueKey)) {
        seen.add(uniqueKey);
        uniqueDocuments.push(doc);
      }
    });

    return uniqueDocuments;
  }


  public showDebugInfo: boolean = false;
  public canUseDebugOptions: boolean = false;

  public isCurrentDocument(doc: Document): boolean {
    // @ts-ignore
    const serialToUse = this.data?.serial ?? this.data?.serials;
    return doc.serial === serialToUse &&
      doc.documentTypeName === this.data?.type?.name;
  }

  public toggleDebugInfo(): void {
    this.showDebugInfo = !this.showDebugInfo && this.canUseDebugOptions;
  }

  public centerView(): void {
    if (this.flowDocuments.length > 0) {
      this.fCanvas.fitToScreen(PointExtensions.initialize(200, 150), true);
    }
  }

  public async fullScreen(): Promise<void> {
    const container = document.querySelector('.flow-container') as HTMLElement;
    if (container) {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await container.requestFullscreen();
      }
    }

    setTimeout(() => {
      this.centerView();
    }, 100);
  }

  public goToOrder(element: Document): void {
    if (!element?.serial || !element?.documentTypeName) {
      console.warn('Elemento no válido para navegación:', element);
      return;
    }

    this.documentNavigationService.navigateToDocument(element.typeCode, element.serial);
  }

  public isClickable(element: Document): boolean {
    const navInfo = this.documentNavigationService.getNavigationInfo(
      element.typeCode,
      element.serial
    );
    return navInfo.canNavigate;
  }

  public getTooltip(element: Document): string {
    const navInfo = this.documentNavigationService.getNavigationInfo(
      element.typeCode,
      element.serial
    );
    return navInfo.tooltip || 'Ver detalle';
  }

  private getDocumentTypeCode(element: Document): DocumentTypeEnum {
    if (element.typeCode) {
      return element.typeCode as DocumentTypeEnum;
    }

    return '' as DocumentTypeEnum;
  }
}
